import { TextInput, Alert } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { requestForgotPassword } from '../../api/fetch/forgot-password';
import { Confirmation } from '../../components/ui/Confirmation';
import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { useLoadingLabel } from '../../hooks/useLoadingLabel';
import { captureEvent, captureException } from '../../monitoring/logrocket';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { MaxLengths } from '../../utils/constants';
import { PersistentParametersContext } from '../../utils/persistParameterContext';
import { redirectToSignIn } from '../../utils/redirect';
import { emailValidator } from '../../utils/validators';

import { StyledContent, StyledLink, StyledAlertContainer, StyledHeading, StyledButton } from './styles';

export function ForgotPassword() {
  usePageLoadEvent('sign in', 'forgot password');
  const trackEvent = useEvent();
  const [isLoading, setIsLoadingState] = useState(false);
  const [transactionStep, setTransactionStep] = useState('init');
  const [error, setErrorState] = useState<null | string>(null);
  const { getContentByPath } = useContent();
  const { validateField, invalidFields, emptyFields } = useFieldValidation([emailValidator]);
  const { params } = useContext(PersistentParametersContext);

  const forgotPasswordCTA = useLoadingLabel(isLoading, 'forgotPassword.steps.reset.buttons.primaryAction.label');
  const { value, bind } = useInput('', 'email', undefined, validateField);

  const emailError = useMemo(
    (): string | undefined =>
      invalidFields.includes('email') || emptyFields.includes('email')
        ? getContentByPath('forgotPassword.steps.reset.fields.email.validation')
        : undefined,
    [getContentByPath, invalidFields, emptyFields],
  );

  useEffect(() => {
    trackEvent('identity – forgot password - start', 'forgot password process start', 'forgotpstart');
  }, [trackEvent]);

  useEffect(() => {
    if (invalidFields.includes('email')) {
      trackEvent(
        'identity – forgot password - credentials input error',
        'forgot password credentials input error',
        'forgotpinputerror',
        {
          errorField: 'Email Address',
          errorDisplayed: getContentByPath('forgotPassword.steps.reset.fields.email.validation'),
        },
      );
    }
  }, [invalidFields, invalidFields.length, trackEvent, getContentByPath]);

  const requestPasswordReset = useCallback(async () => {
    try {
      captureEvent('Forgot Password', {
        email: String(value),
      });

      setIsLoadingState(true);
      await requestForgotPassword({ username: value });
      setTransactionStep('confirmation');

      trackEvent(
        'identity – forgot password - credentials submit success',
        'forgot password credentials submit success',
        'forgotpsubmitsuccess',
        { forgotPasswordState: 'success', emailValidity: 'true' },
      );

      if (error) {
        setErrorState(null);
      }
    } catch (error) {
      setErrorState(getContentByPath('forgotPassword.errors.default'));
      trackEvent(
        'identity – forgot password - credentials submit error',
        'forgot password credentials submit error',
        'forgotpsubmitfail',
        { forgotPasswordState: 'failed', errorDisplayed: getContentByPath('forgotPassword.errors.default') },
      );
      captureException(error as Error, 'forgot-password', { username: value });
    } finally {
      setIsLoadingState(false);
    }
  }, [value, error, getContentByPath, trackEvent]);

  const canRequestSignIn = useMemo(
    () => !invalidFields.length && value && !isLoading,
    [invalidFields.length, isLoading, value],
  );

  if (transactionStep === 'confirmation') {
    return (
      <Confirmation
        title={getContentByPath('forgotPassword.steps.confirmation.title')}
        description={{
          __html: (getContentByPath('forgotPassword.steps.confirmation.body') as string).replace(
            '%email%',
            value ?? '',
          ),
        }}
        redirectCTA={
          params.webview
            ? undefined
            : {
                label: getContentByPath('forgotPassword.steps.confirmation.buttons.primaryAction.label'),
                action: async () => {
                  await trackEvent(
                    'identity – forgot password - back to sign in',
                    'back to sign in',
                    'fpbacktosignin',
                    {},
                    true,
                  );
                  redirectToSignIn();
                },
              }
        }
      />
    );
  }

  return (
    <StyledContent>
      <PageLayout
        testId='route-/forgot-password'
        title={getContentByPath('forgotPassword.steps.reset.title')}
        description={getContentByPath('forgotPassword.steps.reset.description')}
      >
        <Container height='auto'>
          <Logo />
          <StyledHeading variant='heading-medium-3' as='h1' testId='page-title'>
            {getContentByPath('forgotPassword.steps.reset.title')}
          </StyledHeading>
          {error && (
            <StyledAlertContainer>
              <Alert open variant='error' message={error} testId='forgot-password-error' hiddenCloseButton />
            </StyledAlertContainer>
          )}

          <TextInput
            variant='default'
            className={`identity__field email ${emailError ? 'with-error' : ''}`}
            label={getContentByPath('forgotPassword.steps.reset.fields.email.label')}
            id='email'
            disabled={isLoading}
            error={!!emailError}
            hint={emailError}
            {...bind}
            maxLength={MaxLengths.Email}
            autoComplete='email'
            type='email'
          />
          <StyledButton
            label={forgotPasswordCTA}
            variant='primary'
            size='large'
            onClick={requestPasswordReset}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                requestPasswordReset();
              }
            }}
            disabled={!canRequestSignIn}
            className='identity__form-submit'
            loading={isLoading}
            fullWidth
            type='submit'
          />
          {!params.webview && (
            <StyledLink
              className='identity__secondary-action center'
              label={getContentByPath('forgotPassword.steps.reset.buttons.secondaryAction.label')}
              size='large'
              variant='tertiary'
              icon='arrow-left'
              fullWidth
              onClick={async () => {
                await trackEvent(
                  'identity – forgot password - back to sign in',
                  'back to sign in',
                  'fpbacktosignin',
                  {},
                  true,
                );
                navigate('/');
              }}
              onKeyDown={async e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  await trackEvent(
                    'identity – forgot password - back to sign in',
                    'back to sign in',
                    'fpbacktosignin',
                    {},
                    true,
                  );
                  navigate('/');
                }
              }}
            />
          )}
        </Container>
      </PageLayout>
    </StyledContent>
  );
}

ForgotPassword.displayName = 'ForgotPassword';
